import * as React from 'react';
import { VaiIcon } from '@vaisala-rockhopper/design-tokens';
import { Bem, addClass } from '../../utils';
import { Heading } from '../Heading/Heading';
import { FlexItem } from '../Layout/FlexItem';
import { Icon } from '../Icon/Icon';
import { useId } from '../../utils/hooks/useId';
import './Accordion.css';
const EXPANSION_DURATION = 400;
const COLLAPSE_START_DELAY = 10;
export var AnimationState;
(function (AnimationState) {
    AnimationState["collapsed"] = "collapsed";
    AnimationState["expanding"] = "expanding";
    AnimationState["expanded"] = "expanded";
    AnimationState["preparingToCollapse"] = "preparing-to-collapse";
})(AnimationState || (AnimationState = {}));
const bem = Bem.create('vai-accordion');
export const Accordion = React.memo(React.forwardRef((props, ref) => {
    const { htmlId, title, icon, isExpanded, onChangeAccordionExpanded, accordionKey, dataTa = 'vai-accordion', summary, summaryWidth = '30%', children, mode = 'auto', className, onAccordionFocus, _buttonRef, } = props;
    const id = useId(htmlId, ['accordion']);
    const hasIcon = icon ? 'has-icon' : '';
    const containerClass = addClass(bem.block(isExpanded && 'expanded', mode, hasIcon), className);
    const innerContentModifier = isExpanded ? undefined : 'hidden';
    const linkId = id.copyWith(['title', 'link']).toString();
    const headingId = id.copyWith(['title']).toHtmlId();
    const contentId = id.copyWith(['content']).toString();
    const animationState = useAccordionAnimation(isExpanded);
    const handleFocus = React.useCallback(() => {
        if (onAccordionFocus) {
            onAccordionFocus(accordionKey);
        }
    }, [onAccordionFocus, accordionKey]);
    const handleClick = React.useCallback(() => {
        if (onChangeAccordionExpanded) {
            onChangeAccordionExpanded(!isExpanded, accordionKey);
        }
    }, [onChangeAccordionExpanded, isExpanded, accordionKey]);
    return (React.createElement("div", { id: id.toString(), "data-ta": dataTa, className: containerClass, ref: ref },
        React.createElement("button", { id: linkId, "data-ta": 'button', className: bem.element('link')(isExpanded && 'expanded'), onClick: handleClick, "aria-expanded": isExpanded ? 'true' : 'false', "aria-controls": contentId, type: "button", onFocus: handleFocus, ref: _buttonRef },
            React.createElement(Heading, { htmlId: headingId, className: bem.element('title')(), level: 4 },
                React.createElement("div", { className: bem.element('title')('icon-wrapper') },
                    React.createElement(Icon, { className: bem.element('title')('icon'), name: isExpanded ? VaiIcon.ChevronDown : VaiIcon.ChevronRight })),
                title),
            summary && (React.createElement(FlexItem, { flexBasis: summaryWidth, className: bem.element('summary')() }, summary)),
            icon && React.createElement("div", { className: bem.element('secondary-icon')() }, icon)),
        React.createElement("div", { id: contentId, className: bem.element('content')(animationState), "aria-labelledby": headingId && headingId.toString() },
            React.createElement("div", { className: bem.element('inner-content')(innerContentModifier) }, children))));
}));
function useAccordionAnimation(isExpanded) {
    const [animationState, setAnimationState] = React.useState(isExpanded ? AnimationState.expanded : AnimationState.collapsed);
    const timeoutIdRef = React.useRef();
    const setDelayedAnimationState = React.useCallback((state, delay) => {
        if (timeoutIdRef.current)
            window.clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = window.setTimeout(() => setAnimationState(state), delay);
    }, []);
    React.useEffect(() => {
        const shouldExpand = animationState === AnimationState.collapsed && isExpanded;
        const shouldCollapse = animationState === AnimationState.expanded && !isExpanded;
        if (shouldExpand) {
            setAnimationState(AnimationState.expanding);
            setDelayedAnimationState(AnimationState.expanded, EXPANSION_DURATION);
        }
        else if (shouldCollapse) {
            setAnimationState(AnimationState.preparingToCollapse);
            setDelayedAnimationState(AnimationState.collapsed, COLLAPSE_START_DELAY);
        }
        return () => {
            timeoutIdRef.current && clearTimeout(timeoutIdRef.current);
        };
    }, [isExpanded, animationState, setAnimationState, setDelayedAnimationState]);
    return animationState;
}
//# sourceMappingURL=Accordion.js.map